// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { fetchAssignmentById } from '../../../services/assignments';
import './Assignments.css';
import { submissionStatusFor, timelinessComponents, timelinessStandard } from '../../../utils/taskTimeliness';

export default function AssignmentScoringPrintout() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const teacherId = user?.user_id || user?.id || null;

  const [assignment, setAssignment] = useState<any>(null);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [mode, setMode] = useState('completed'); // 'completed' | 'blank'

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const data = await fetchAssignmentById(assignmentId, teacherId);
        setAssignment(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load assignment for printing');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [assignmentId, teacherId]);

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading printable scoring grid...</div>;
  }

  if (error || !assignment) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>{error || 'Assignment not found'}</p>
        <button type="button" onClick={() => navigate('/teacher/assignments')}>
          Return to Assignments Hub
        </button>
      </div>
    );
  }

  const standards = assignment.standards || [];
  const students = assignment.students || [];
  const grades = assignment.grades || {};
  const timingComponents = timelinessComponents(assignment);

  return (
    <div style={{ padding: '24px', background: '#ffffff', minHeight: '100vh', fontFamily: 'sans-serif' }}>
      {/* On-screen controls (hidden during actual browser print) */}
      <div className="lms-toolbar" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <button
              type="button"
              className="lms-btn-secondary lms-btn-sm"
              onClick={() => navigate(`/teacher/assignments/${assignment.id}`)}
              style={{ marginRight: '10px' }}
            >
              ← Back to Grading Grid
            </button>
            <strong style={{ fontSize: '1rem', color: '#1e3a8a' }}>Print Scoring Grid</strong>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="radio"
                name="printMode"
                value="completed"
                checked={mode === 'completed'}
                onChange={() => setMode('completed')}
              />
              Completed Grid (with scores)
            </label>

            <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="radio"
                name="printMode"
                value="blank"
                checked={mode === 'blank'}
                onChange={() => setMode('blank')}
              />
              Blank Grid (for manual / clipboard grading)
            </label>

            <button
              type="button"
              className="lms-btn-primary"
              onClick={() => window.print()}
            >
              🖨️ Print Now
            </button>
          </div>
        </div>
      </div>

      {/* ── PRINTABLE SHEET LAYOUT (Designed for 8.5x11 Landscape) ─────────── */}
      <div className="lms-print-document" style={{ color: '#000000' }}>
        {/* Document Header */}
        <div style={{ borderBottom: '2px solid #000000', paddingBottom: '12px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 style={{ margin: '0 0 4px', fontSize: '1.4rem', fontWeight: 800 }}>
                {assignment.title}
              </h1>
              <div style={{ fontSize: '0.9rem', color: '#333333' }}>
                <strong>Class:</strong> {assignment.className} &nbsp;|&nbsp; <strong>Subject:</strong> {assignment.subject} &nbsp;|&nbsp; <strong>Type:</strong> {assignment.type}
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '0.85rem' }}>
              <div><strong>Due Date:</strong> {assignment.dueDate || '—'}</div>
              <div><strong>Generated:</strong> {new Date().toLocaleDateString()}</div>
              <div><strong>Academic Eye LMS</strong></div>
            </div>
          </div>

          {/* Scoring Scale Legend */}
          <div style={{ marginTop: '10px', fontSize: '0.78rem', display: 'flex', gap: '16px', background: '#f5f5f5', padding: '6px 10px', border: '1px solid #cccccc' }}>
            <span><strong>Scoring Scale:</strong></span>
            <span><strong>4:</strong> Proficient (Independent)</span>
            <span><strong>3:</strong> On Track (Target Level)</span>
            <span><strong>2:</strong> Learning Zone (With Support)</span>
            <span><strong>1:</strong> Not Yet</span>
            <span><strong>M:</strong> Missing</span>
            <span><strong>NA:</strong> Not Assessed</span>
          </div>
        </div>

        {/* Assessed Standards Legend */}
        <div style={{ marginBottom: '16px', fontSize: '0.8rem', border: '1px solid #cccccc', padding: '8px 12px' }}>
          <div style={{ fontWeight: 700, marginBottom: '4px' }}>Assessed Standards:</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '8px' }}>
            {standards.map((std) => (
              <div key={std.mlsCode}>
                <strong>{std.mlsCode}:</strong> {std.friendlyTitle || std.description}
              </div>
            ))}
          </div>
        </div>

        {/* Table Matrix */}
        {assignment.timeliness?.enabled && (
          <p>Submission timing: On Time 95 / Late 70 / Incomplete 45.
            {' '}Success Skills: {timelinessStandard(assignment.timeliness.grade || assignment.grade)}</p>
        )}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.85rem',
            border: '1px solid #000000',
          }}
        >
          <thead>
            <tr style={{ background: '#f0f0f0' }}>
              <th style={{ border: '1px solid #000000', padding: '8px', textAlign: 'left', width: '220px' }}>
                Student Name
              </th>
              {standards.map((std) => (
                <th
                  key={std.mlsCode}
                  style={{
                    border: '1px solid #000000',
                    padding: '8px',
                    textAlign: 'center',
                    minWidth: '100px',
                  }}
                >
                  <div style={{ fontWeight: 800 }}>{std.mlsCode}</div>
                  <div style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>
                    {std.friendlyTitle ? std.friendlyTitle.slice(0, 24) : ''}
                  </div>
                </th>
              ))}
              {timingComponents.map((component) => (
                <th key={`timing-${component.key}`} style={{ border: '1px solid #000000', padding: '8px' }}>
                  Submission: {component.label}
                </th>
              ))}
              <th style={{ border: '1px solid #000000', padding: '8px', textAlign: 'left', width: '200px' }}>
                Teacher Observations & Notes
              </th>
            </tr>
          </thead>
          <tbody>
            {students.map((stu) => {
              const stuGrades = grades[stu.studentId] || {};
              const stuNote = assignment.studentNotes?.[stu.studentId] || '';

              return (
                <tr key={stu.studentId} style={{ pageBreakInside: 'avoid' }}>
                  <td style={{ border: '1px solid #000000', padding: '8px', fontWeight: 600 }}>
                    {stu.name}
                    <div style={{ fontSize: '0.7rem', color: '#555555' }}>{stu.studentNumber}</div>
                  </td>

                  {standards.map((std) => {
                    const cell = stuGrades[std.mlsCode];
                    const displayVal = mode === 'blank'
                      ? ''
                      : cell?.isMissing
                      ? 'M'
                      : cell?.notAssessed
                      ? 'NA'
                      : cell?.score !== undefined && cell?.score !== null && cell?.score !== ''
                      ? cell.score
                      : '—';

                    return (
                      <td
                        key={std.mlsCode}
                        style={{
                          border: '1px solid #000000',
                          padding: '8px',
                          textAlign: 'center',
                          height: mode === 'blank' ? '32px' : 'auto',
                          fontWeight: 700,
                        }}
                      >
                        {displayVal}
                      </td>
                    );
                  })}

                  {timingComponents.map((component) => (
                    <td key={`timing-${component.key}`} style={{ border: '1px solid #000000', padding: '8px' }}>
                      {mode === 'blank' ? '' : submissionStatusFor(assignment, stu.studentId, component.key).label}
                    </td>
                  ))}
                  <td style={{ border: '1px solid #000000', padding: '8px', fontSize: '0.78rem' }}>
                    {mode === 'blank' ? '' : stuNote}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
